import { z } from 'zod';

/**
 * What a Study Type asks for at Materials, and which facts it takes out of what arrives.
 *
 * It lives on the template version rather than beside it, so the version a study is pinned to
 * decides its intake and its facts the same way it decides everything else the type says, and the
 * content pin in `study-types.test.ts` covers it. It sits in its own module because the template
 * registry and the Materials decisions both read it, and `materials.ts` reads the registry.
 */

/**
 * A fact's id, which a stored correction and a model's answer both key by.
 *
 * Lower-case words joined by underscores, short enough to read in a stored document. The alphabet
 * still admits `constructor`, so every record keyed by a fact id is read with `Object.hasOwn`
 * rather than by indexing.
 */
export const factIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/)
  .max(64);
export type FactId = z.infer<typeof factIdSchema>;

/** A form field's id, spelled like a fact's for the same reason. */
export const formFieldIdSchema = factIdSchema;
export type FormFieldId = FactId;

/** The most entries a list field may declare, whatever its template says. */
export const MAX_FORM_FIELD_LIST_ITEMS = 20;

/** The longest value a fact row may hold, whether Sigora read it or the researcher typed it. */
export const MAX_FACT_VALUE_CHARS = 500;

/** The longest single entry in a list field. */
export const MAX_MATERIALS_LIST_ITEM_CHARS = 98;

/** How a list field's entries are joined into the one value of the fact it fills. */
export const LIST_SEPARATOR = ', ';

/**
 * Whether a list field filling a fact can be filled to its `maxItems`: the joined list is that
 * fact's value, so a full list of entries at their longest must fit one.
 */
const listCapacityFits = (maxItems: number): boolean =>
  maxItems * MAX_MATERIALS_LIST_ITEM_CHARS + (maxItems - 1) * LIST_SEPARATOR.length <=
  MAX_FACT_VALUE_CHARS;

/**
 * The longest label a fact, a form field or a paired version may carry. A paired input repeats its
 * shape's labels, so both are bounded by this one number.
 */
export const MAX_MATERIALS_LABEL_CHARS = 200;

const labelSchema = z.string().min(1).max(MAX_MATERIALS_LABEL_CHARS);

export const materialsFactSchema = z
  .object({
    id: factIdSchema,
    /** What the row is called on the Materials screen and in the request for a missing fact. */
    label: labelSchema,
    /**
     * Whether the Materials may be confirmed without it. A required fact with no value is what the
     * actionable request names; nothing is invented to fill it.
     */
    required: z.boolean(),
  })
  .readonly();
export type MaterialsFact = z.infer<typeof materialsFactSchema>;

export const formFieldSchema = z
  .object({
    id: formFieldIdSchema,
    label: labelSchema,
    /**
     * The fact this field fills verbatim. The model is never asked for a fact a field fills, so a
     * typed value is not paraphrased and a fully-filled form needs no model call at all.
     */
    fillsFact: factIdSchema.optional(),
    /**
     * Present on a list field, which takes up to this many entries rather than one line. On a
     * field that fills a fact it must be a number a researcher can reach: that many entries at
     * their longest have to join into one fact value, which the shape checks.
     */
    maxItems: z.int().positive().max(MAX_FORM_FIELD_LIST_ITEMS).optional(),
    /** Whether a save may leave the field empty. */
    required: z.boolean(),
  })
  .readonly();
export type FormField = z.infer<typeof formFieldSchema>;

export const INTAKE_KINDS = ['text', 'form', 'paired'] as const;
export const intakeKindSchema = z.enum(INTAKE_KINDS);
export type IntakeKind = z.infer<typeof intakeKindSchema>;

export const materialsIntakeSchema = z
  .discriminatedUnion('kind', [
    /** A sentence or a pasted brief. */
    z.object({ kind: z.literal('text') }),
    /** Labelled fields, in the order the screen shows them. */
    z.object({
      kind: z.literal('form'),
      fields: z.array(formFieldSchema).min(1).readonly(),
    }),
    /**
     * Two versions described side by side, and the one thing that differs between them. The
     * differs line fills `differsFact` when it is given; when it is not, that fact is extracted.
     */
    z.object({
      kind: z.literal('paired'),
      labels: z.tuple([labelSchema, labelSchema]).readonly(),
      differsFact: factIdSchema.optional(),
    }),
  ])
  .readonly();
export type MaterialsIntake = z.infer<typeof materialsIntakeSchema>;

const refuseRepeated = (
  ids: readonly string[],
  path: (string | number)[],
  context: z.RefinementCtx,
): void => {
  const seen = new Set<string>();

  ids.forEach((id, index) => {
    if (seen.has(id)) {
      context.addIssue({
        code: 'custom',
        path: [...path, index, 'id'],
        message: `duplicate id ${id}`,
      });
    }

    seen.add(id);
  });
};

/**
 * The whole Materials block of a template version.
 *
 * The facts are ordered, and the order is the order of the rows. Every reference from the intake
 * to a fact must name one the block holds, and no fact may be filled by two inputs — either would
 * leave a row whose value depends on which input a reader happened to look at first.
 */
export const materialsShapeSchema = z
  .object({
    intake: materialsIntakeSchema,
    facts: z.array(materialsFactSchema).min(1).readonly(),
  })
  .superRefine((shape, context) => {
    refuseRepeated(
      shape.facts.map((fact) => fact.id),
      ['facts'],
      context,
    );

    const factIds = new Set(shape.facts.map((fact) => fact.id));
    const filled = new Set<string>();
    const noteFilled = (factId: string, path: (string | number)[]): void => {
      if (!factIds.has(factId)) {
        context.addIssue({ code: 'custom', path, message: `no fact ${factId} to fill` });
      }

      if (filled.has(factId)) {
        context.addIssue({ code: 'custom', path, message: `${factId} is filled twice` });
      }

      filled.add(factId);
    };

    if (shape.intake.kind === 'form') {
      refuseRepeated(
        shape.intake.fields.map((field) => field.id),
        ['intake', 'fields'],
        context,
      );
      shape.intake.fields.forEach((field, index) => {
        if (field.fillsFact !== undefined) {
          noteFilled(field.fillsFact, ['intake', 'fields', index, 'fillsFact']);
        }

        if (
          field.fillsFact !== undefined &&
          field.maxItems !== undefined &&
          !listCapacityFits(field.maxItems)
        ) {
          context.addIssue({
            code: 'custom',
            path: ['intake', 'fields', index, 'maxItems'],
            message: `a full list filling a fact must join to at most ${String(MAX_FACT_VALUE_CHARS)} characters`,
          });
        }
      });
    }

    if (shape.intake.kind === 'paired' && shape.intake.differsFact !== undefined) {
      noteFilled(shape.intake.differsFact, ['intake', 'differsFact']);
    }
  })
  .readonly();
export type MaterialsShape = z.infer<typeof materialsShapeSchema>;
